import { getApiClient } from '@src/services/api-client'
import { MutationFunction, useMutation, useQueryClient } from '@tanstack/react-query'
import { CreditApplication, FullCreditApplication } from '../../types'
import { FinalCreditDecision } from '../../types/CreditDecisionSchema'
import { fullCreditApplicationKeys } from '../full-credit-application/full-credit-application-api'
import { transformCreditApplicationFromApi } from '../credit-application/credit-transform'

function transformToApi(decision: FinalCreditDecision): FinalCreditDecision {
  const update = { ...decision }
  if (update.existingCarLoanStatus === '') update.existingCarLoanStatus = null
  if (update.maxAmountFinanced === null) update.maxAmountFinanced = 0
  return update
}

const BASE_URL = '/Applications/{financingProgramId}/{creditApplicationId}/FinalDecision'

// API Functions
export async function updateFinalCreditDecision(
  data: FinalCreditDecision & { creditApplicationId: string },
): Promise<CreditApplication> {
  const apiClient = getApiClient()
  const transformedData = transformToApi(data)
  const url = BASE_URL.replace('{financingProgramId}', String(data.financingProgramId)).replace(
    '{creditApplicationId}',
    data.creditApplicationId,
  )
  const response = await apiClient.put<CreditApplication>(url, transformedData)
  return response.data
}

// React-Query Hooks
export function useUpdateFinalCreditDecision(): [
  MutationFunction<CreditApplication, FinalCreditDecision & { creditApplicationId: string }>,
  boolean,
  Error | null,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, error, reset } = useMutation({
    mutationFn: updateFinalCreditDecision,
    onSuccess: (updatedCredit, params) => {
      const queryKey = fullCreditApplicationKeys.detail(params.creditApplicationId, params.financingProgramId!)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          credit: transformCreditApplicationFromApi(updatedCredit),
        })
      }
    },
  })
  return [mutateAsync, isPending, error, reset]
}
